import { CaptionBold } from '@comeon/ui';
import styled from 'styled-components';
import useGetRelativeDate, { capitalizeFirstLetter, isDateToday, isDateTomorrow } from '~/hooks/use-get-relative-date';
import { LiveGameStateDto } from '../../../api/sbapi/sbapi-api-model';
import SportsbookLiveGameState from '../../live-game-state/sportsbook-live-game-state';
import { formatEventDateAndTime } from '~/components/sportsbook/shared/utils/date-utils';

const Wrapper = styled.div<{
	isLive: boolean;
}>`
	display: flex;
	align-items: center;
	justify-content: center;

	small {
		color: ${({ isLive }) =>
			isLive
				? `var(--main-badge)`
				: `var(--main-text-supportive)`}; // In case of live event the element gets proper brand live colour applied

		span {
			font-weight: 400;
			margin-left: 2px;
			min-width: 0;
		}
	}
`;

const EventLiveGameState = styled(SportsbookLiveGameState)`
	color: var(--main-badge);
	small {
		padding: 0 2px;
	}
`;

const UpcomingGameTime = styled(CaptionBold)`
	span {
		font-weight: 400;
	}
`;

const options = {
	formatWeekdayOptions: { weekday: 'short' },
	formatMonthOptions: { month: 'short' }
} as const;

type Props = {
	isLive: boolean;
	liveGameState?: LiveGameStateDto;
	lastUpdatedOn: string;
	startingOn: string;
};

const GameCardGameTime: React.FC<Props> = ({ isLive, liveGameState, startingOn, lastUpdatedOn }) => {
	const getRelativeDate = useGetRelativeDate(options);
	const { relativeDay, numericDay, month } = getRelativeDate(startingOn);
	const isStartOnTodayOrTomorrow = isDateToday(startingOn) || isDateTomorrow(startingOn);
	const capitalizedMonth = capitalizeFirstLetter(month);
	const hasLiveGameTime = isLive && !!liveGameState;
	const upcomingGameTime = isStartOnTodayOrTomorrow
		? `${relativeDay}`
		: `${relativeDay} ${numericDay} ${capitalizedMonth}`;

	if (hasLiveGameTime) {
		return (
			<Wrapper isLive>
				<EventLiveGameState liveGameState={liveGameState} lastUpdatedOn={lastUpdatedOn} />
			</Wrapper>
		);
	}

	return (
		<Wrapper isLive={false}>
			<UpcomingGameTime>
				{upcomingGameTime}
				<span>
					{formatEventDateAndTime(startingOn, {
						hasTimeFormat: true,
						hasDateFormat: false
					})}
				</span>
			</UpcomingGameTime>
		</Wrapper>
	);
};

export default GameCardGameTime;
